/**
 * ─── Interaction audio bridge ─────────────────────────────────────────────────
 *
 * Tiny module-level pub/sub carrying committed navigation interactions from UI
 * components to the audio layer, keeping the hard rule intact: UI components
 * never import Tone or the siteAudio* controller — they publish here, and
 * BackgroundAudioProvider (the single owner of all engine calls) subscribes.
 * Mirrors manifestoBackground.js / homeSection.js.
 *
 *   publishProjectPreviewChange(slug)  the previewed PROJECTS item genuinely changed
 *                                      (desktop hover, keyboard focus, mobile swipe).
 *   publishProjectCanvasTransition(fromSlug, toSlug)
 *                                      the PROJECTS canvas began a real image→image
 *                                      morph (Sketch onTransitionStart hook).
 *   publishProjectOpen(slug)           a project detail view opened (per keyed mount).
 *   publishProjectClose(slug)          the detail view was explicitly closed (← WORK).
 *   publishLabNavigate(direction)      one committed LAB carousel move (−1/0/+1).
 *
 * All of these are transient one-shots: before the audio engine exists (pre-
 * gesture) they are dropped, never buffered — consistent with hover/transition.
 * ──────────────────────────────────────────────────────────────────────────── */

const listeners = new Set();

// Dev-only publish counters — lets a console session prove that real UI
// interactions reach this bridge (window.__KessonInteractionPublished).
// Compare against window.__KessonAudio.counts() (engine side): published >
// engine "called" ⇒ the provider gate (reduced motion) blocked the event;
// "called" > "played" ⇒ an engine guard (not started / muted / throttle /
// same-slug) swallowed it. CRA inlines NODE_ENV, so this is dead-code-
// eliminated from production builds.
const IS_DEV = process.env.NODE_ENV === 'development';
function countPublish(key) {
    if (!IS_DEV || typeof window === 'undefined') return;
    if (!window.__KessonInteractionPublished) {
        window.__KessonInteractionPublished = { preview: 0, canvasTransition: 0, open: 0, close: 0, labNav: 0 };
    }
    window.__KessonInteractionPublished[key] += 1;
}

export function publishProjectPreviewChange(slug) {
    countPublish('preview');
    listeners.forEach((h) => { if (h.onProjectPreviewChange) h.onProjectPreviewChange(slug); });
}

export function publishProjectCanvasTransition(fromSlug, toSlug) {
    countPublish('canvasTransition');
    listeners.forEach((h) => { if (h.onProjectCanvasTransition) h.onProjectCanvasTransition(fromSlug, toSlug); });
}

export function publishProjectOpen(slug) {
    countPublish('open');
    listeners.forEach((h) => { if (h.onProjectOpen) h.onProjectOpen(slug); });
}

export function publishProjectClose(slug) {
    countPublish('close');
    listeners.forEach((h) => { if (h.onProjectClose) h.onProjectClose(slug); });
}

export function publishLabNavigate(direction) {
    countPublish('labNav');
    listeners.forEach((h) => { if (h.onLabNavigate) h.onLabNavigate(direction); });
}

/**
 * Subscribe with any subset of
 * { onProjectPreviewChange, onProjectCanvasTransition, onProjectOpen,
 *   onProjectClose, onLabNavigate }.
 * Returns an unsubscribe function.
 */
export function subscribeInteractionAudio(handlers) {
    listeners.add(handlers);
    return () => { listeners.delete(handlers); };
}
